import { useState, useContext } from 'react';
import {
  Box,
  FormLabel,
  FormGroup,
  TextField,
  Typography,
  Button,
  Alert,
  AlertTitle
} from '@mui/material';
import { LocalizationProvider, DateRangePicker, DateRange } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { startOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { useOktaAuth } from '@okta/okta-react';

import { AuthContext, AuthContextType } from 'src/contexts/AuthContext';
import { downloadAllFeedbackReport } from 'src/api';

export const ChatbotAdmin = () => {
  const { isCpdPilot, activeMemberId } = useContext(AuthContext) as AuthContextType;
  const [dateDateArray, setDateDateArray] = useState<DateRange<Date>>([
    startOfMonth(new Date()),
    endOfMonth(new Date())
  ]);
  const [error, setError] = useState<string | null>('');
  const { authState } = useOktaAuth();

  const handleChange = (dateDateArray: DateRange<Date>) => {
    setDateDateArray(dateDateArray);
  };

  const handleDownloadClick = () => {
    setError(null);
    downloadAllFeedbackReport({
      memberid: authState?.accessToken?.accessToken ?? '',
      token: activeMemberId ?? '',
      fromDate: dateDateArray[0]?.toISOString().slice(0, 10) + 'T00:00:00Z',
      toDate: dateDateArray[1]?.toISOString().slice(0, 10) + 'T00:00:00Z',
      onerror: (error) => {
        let errorMessage = 'There was an unexpected error.';
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        setError(errorMessage);
      }
    });
  };

  if (!isCpdPilot) {
    return;
  }

  return (
    <Box>
      <FormGroup sx={{ fontWeight: 600, mb: 1 }}>
        <FormLabel sx={{ fontWeight: 600, mb: 2 }}>Download Chatbot Feedback</FormLabel>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          localeText={{ start: 'Start date', end: 'End date' }}
        >
          <DateRangePicker
            value={dateDateArray}
            onChange={handleChange}
            minDate={startOfDay(new Date('2025-03-01'))}
            inputFormat="dd/MM/yyyy"
            renderInput={(startProps, endProps) => (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: { sm: 'wrap', lg: 'nowrap' }
                  }}
                >
                  <TextField {...startProps} sx={{ maxWidth: 120, my: 1 }} />
                  <Box
                    sx={{
                      mx: 2,
                      '@media screen and (min-width: 900px) and (max-width: 1083px) ': {
                        opacity: 0
                      }
                    }}
                  >
                    <Typography variant="body1"> to </Typography>
                  </Box>
                  <TextField {...endProps} sx={{ maxWidth: 120, my: 1 }} />
                </Box>
              </>
            )}
          />
        </LocalizationProvider>
      </FormGroup>
      <Button variant="contained" color="primary" fullWidth={false} onClick={handleDownloadClick}>
        Download
      </Button>
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          <AlertTitle>Error</AlertTitle>
          <Typography variant={'body2'}>{error}</Typography>
        </Alert>
      )}
    </Box>
  );
};
